/**
 * Structured clinical fields for the admin client editor.
 *
 * These are safety-critical fields (allergies, code status, fall risk, etc.)
 * that previously lived buried inside a free-text note field. Surfacing them
 * as typed enums makes them queryable + lets the FactSheet display them as
 * scannable badges + warning banners.
 */
import { Plus, Trash2 } from "lucide-react";

export const DIET_OPTIONS = [
  { value: "", label: "—" },
  { value: "regular", label: "Regular" },
  { value: "diabetic", label: "Diabetic" },
  { value: "low_sodium", label: "Low sodium" },
  { value: "soft", label: "Soft" },
  { value: "pureed", label: "Pureed" },
  { value: "thickened_liquids", label: "Thickened liquids" },
  { value: "npo", label: "NPO (nothing by mouth)" },
  { value: "tube_feeding", label: "Tube feeding" },
];

export const MOBILITY_OPTIONS = [
  { value: "", label: "—" },
  { value: "independent", label: "Independent" },
  { value: "cane", label: "Cane" },
  { value: "walker", label: "Walker" },
  { value: "wheelchair", label: "Wheelchair" },
  { value: "bedbound", label: "Bedbound" },
];

export const COGNITION_OPTIONS = [
  { value: "", label: "—" },
  { value: "alert", label: "Alert" },
  { value: "mild_impairment", label: "Mild impairment" },
  { value: "moderate_impairment", label: "Moderate impairment" },
  { value: "severe_impairment", label: "Severe impairment" },
  { value: "non_verbal", label: "Non-verbal" },
];

export const FALL_RISK_OPTIONS = [
  { value: "", label: "—" },
  { value: "low", label: "Low" },
  { value: "moderate", label: "Moderate" },
  { value: "high", label: "High" },
];

export const CODE_STATUS_OPTIONS = [
  { value: "", label: "Not documented" },
  { value: "full_code", label: "Full code" },
  { value: "dnr", label: "DNR" },
  { value: "dni", label: "DNI" },
  { value: "dnr_dni", label: "DNR + DNI" },
  { value: "comfort_only", label: "Comfort only" },
];

export const GENDER_OPTIONS = [
  { value: "", label: "—" },
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "non_binary", label: "Non-binary" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

export const ALLERGY_SEVERITY = ["mild", "moderate", "severe", "anaphylactic"];

function AllergyRow({ allergy, onChange, onRemove, index }) {
  return (
    <div className="grid grid-cols-12 gap-2" data-testid={`allergy-row-${index}`}>
      <input
        className="input-clinical col-span-4 text-sm"
        placeholder="Substance"
        value={allergy.substance}
        onChange={(e) => onChange({ ...allergy, substance: e.target.value })}
        data-testid={`allergy-${index}-substance`}
      />
      <input
        className="input-clinical col-span-4 text-sm"
        placeholder="Reaction"
        value={allergy.reaction || ""}
        onChange={(e) => onChange({ ...allergy, reaction: e.target.value })}
        data-testid={`allergy-${index}-reaction`}
      />
      <select
        className="input-clinical col-span-3 text-sm"
        value={allergy.severity}
        onChange={(e) => onChange({ ...allergy, severity: e.target.value })}
        data-testid={`allergy-${index}-severity`}
      >
        {ALLERGY_SEVERITY.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <button
        type="button"
        onClick={onRemove}
        className="col-span-1 flex items-center justify-center rounded-lg border border-clinical-border text-red-600 hover:bg-red-50"
        data-testid={`allergy-${index}-remove`}
        aria-label="Remove allergy"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function AllergyList({ allergies, onChange }) {
  const add = () => onChange([
    ...(allergies || []),
    { substance: "", reaction: "", severity: "moderate" },
  ]);
  const update = (i, next) => {
    const arr = [...allergies];
    arr[i] = next;
    onChange(arr);
  };
  const remove = (i) => onChange(allergies.filter((_, idx) => idx !== i));
  const filled = (allergies || []).filter((a) => (a.substance || "").trim());
  return (
    <div className="space-y-2">
      {filled.length === 0 && (
        <div className="rounded-lg border border-dashed border-clinical-border p-3 text-center text-xs text-clinical-subtext">
          No allergies on file. Click "Add allergy" to record any.
        </div>
      )}
      {(allergies || []).map((a, i) => (
        <AllergyRow
          key={i}
          index={i}
          allergy={a}
          onChange={(next) => update(i, next)}
          onRemove={() => remove(i)}
        />
      ))}
      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1.5 rounded-lg border border-clinical-border bg-white px-3 py-1.5 text-xs font-medium text-clinical-text hover:border-clinical-primary"
        data-testid="allergy-add"
      >
        <Plus size={12} /> Add allergy
      </button>
    </div>
  );
}

/** Single-select dropdown bound to one form field. */
function EnumField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="label-eyebrow mb-1.5 block" htmlFor={testid}>{label}</label>
      <select
        id={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-clinical"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

/** Identity + clinical-baseline fields. Renders inside the existing client modal. */
export default function ClinicalFields({ form, setForm }) {
  return (
    <div className="space-y-4 rounded-xl border border-clinical-border bg-clinical-bg/30 p-4">
      <div className="label-eyebrow flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
        Clinical baseline
      </div>
      <div className="grid grid-cols-2 gap-3">
        <EnumField
          label="Gender" value={form.gender}
          onChange={(v) => setForm({ ...form, gender: v })}
          options={GENDER_OPTIONS} testid="client-gender"
        />
        <div>
          <label className="label-eyebrow mb-1.5 block" htmlFor="client-pronouns">Pronouns</label>
          <input
            id="client-pronouns"
            value={form.pronouns}
            onChange={(e) => setForm({ ...form, pronouns: e.target.value })}
            className="input-clinical"
            placeholder="she/her, he/him, they/them…"
            data-testid="client-pronouns"
          />
        </div>
      </div>

      <div>
        <label className="label-eyebrow mb-1.5 block" htmlFor="client-diagnosis">Primary diagnosis</label>
        <input
          id="client-diagnosis"
          value={form.primary_diagnosis}
          onChange={(e) => setForm({ ...form, primary_diagnosis: e.target.value })}
          className="input-clinical"
          placeholder="e.g. Alzheimer's, stage 2 · CHF · Type 2 diabetes"
          data-testid="client-diagnosis"
        />
      </div>

      <div>
        <label className="label-eyebrow mb-1.5 block">Allergies</label>
        <AllergyList
          allergies={form.allergies}
          onChange={(next) => setForm({ ...form, allergies: next })}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <EnumField
          label="Diet" value={form.diet}
          onChange={(v) => setForm({ ...form, diet: v })}
          options={DIET_OPTIONS} testid="client-diet"
        />
        <div>
          <label className="label-eyebrow mb-1.5 block" htmlFor="client-diet-notes">Diet notes</label>
          <input
            id="client-diet-notes"
            value={form.diet_notes}
            onChange={(e) => setForm({ ...form, diet_notes: e.target.value })}
            className="input-clinical"
            placeholder="Optional"
            data-testid="client-diet-notes"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <EnumField
          label="Mobility" value={form.mobility}
          onChange={(v) => setForm({ ...form, mobility: v })}
          options={MOBILITY_OPTIONS} testid="client-mobility"
        />
        <EnumField
          label="Cognition" value={form.cognition}
          onChange={(v) => setForm({ ...form, cognition: v })}
          options={COGNITION_OPTIONS} testid="client-cognition"
        />
        <EnumField
          label="Fall risk" value={form.fall_risk}
          onChange={(v) => setForm({ ...form, fall_risk: v })}
          options={FALL_RISK_OPTIONS} testid="client-fall-risk"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <EnumField
          label="Code status" value={form.code_status}
          onChange={(v) => setForm({ ...form, code_status: v })}
          options={CODE_STATUS_OPTIONS} testid="client-code-status"
        />
        <label className="flex items-end gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={form.advance_directive_on_file}
            onChange={(e) => setForm({ ...form, advance_directive_on_file: e.target.checked })}
            data-testid="client-advance-directive"
          />
          Advance directive on file
        </label>
      </div>
    </div>
  );
}
